import ROUTES from '../../../../constants/routes';
import type { CheckoutPaymentMethod, CheckoutTestimonial, MedicationClaims, PolicyLink } from './types';

// stamped on each siteEvent as its variantId, so a key is permanent once the test takes traffic
export const CHECKOUT_LAYOUT_EXPERIMENT = 'CHECKOUT_LAYOUT';

export const CHECKOUT_LAYOUT = {
	CONTROL: 'CONTROL',
	PLAN_APPROVED: 'PLAN_APPROVED',
	APPROVAL_COUNTDOWN: 'APPROVAL_COUNTDOWN',
} as const;

export type CheckoutLayoutKey = typeof CHECKOUT_LAYOUT[keyof typeof CHECKOUT_LAYOUT];
export type CheckoutLayoutArm = Exclude<CheckoutLayoutKey, typeof CHECKOUT_LAYOUT.CONTROL>;

// allocation is crc32(leadId) % weightSum, so changing the arm count reassigns most visitors. CONTROL is
// the fallback for a visitor the allocator cannot place, never an arm
export const CHECKOUT_LAYOUT_VARIANTS: readonly CheckoutLayoutArm[] = [
	CHECKOUT_LAYOUT.APPROVAL_COUNTDOWN,
	CHECKOUT_LAYOUT.PLAN_APPROVED,
];

// every lead goes to PLAN_APPROVED; APPROVAL_COUNTDOWN keeps its place, with no traffic, so ?checkoutlayout= can still
// preview it and the test can be split again by changing these weights
export const CHECKOUT_LAYOUT_WEIGHTS: readonly number[] = CHECKOUT_LAYOUT_VARIANTS.map((variant) => (variant === CHECKOUT_LAYOUT.PLAN_APPROVED ? 1 : 0));

// the step number names the checkout a lead was shown on every event and on lastFunnelStep, so a number is
// permanent once the test takes traffic; a new arm takes the next free one - 5002 is /f's program checkout
export const CHECKOUT_LAYOUT_STEP_NUMBERS: Record<CheckoutLayoutArm, number> = {
	APPROVAL_COUNTDOWN: 5000,
	PLAN_APPROVED: 5001
};

export const CHECKOUT_LAYOUT_PROGRAM_SLUGS: readonly string[] = ['wl'];

export const CHECKOUT_LAYOUT_QUERY_PARAM = 'checkoutlayout';

export const CHECKOUT_FORM_ID = 'CheckoutForm';
export const BILLING_SAME_LABEL = 'Shipping and billing address are the same';
export const CHECKOUT_PROCESSING_TEXT = 'Processing…';
export const CHECKOUT_PROCESSING_MESSAGE = 'We are working on processing your transaction...Please be patient!';
// every arm's checkout button; a press finds what is missing, or checks out once nothing is
export const CHECKOUT_CONTINUE_TEXT = 'Continue';
// names the button it sits above, so the two are one constant
export const CHECKOUT_LAYOUT_AUTHORIZATION_NOTICE = `By clicking ${CHECKOUT_CONTINUE_TEXT}, I authorize ReadyRX to charge me according to the Terms until I cancel.`;

// a red border marks a field the press found missing; only the address check's own results (firebase/Smarty.js) keep their
// text, since a border cannot say a PO box is refused or an address was not found
export const ADDRESS_VERIFICATION_ERROR_TYPES: readonly string[] = ['invalid', 'unconfirmed', 'unknown'];

export const PAYMENT_METHOD_LABELS: Record<CheckoutPaymentMethod, string> = {
	CARD: 'Card',
	KLARNA: 'Klarna'
};
export const PAYMENT_METHOD_GROUP_LABEL = 'Payment method';

export const MONTH_TO_MONTH_FREQUENCY = 1;
export const WEEKS_PER_MONTH = 4;

export const APPROVAL_HOLD_SECONDS = 10 * 60;
export const APPROVAL_VALID_LABEL = 'Your approval is valid for';
export const APPROVAL_HELD_LABEL = 'Approval held for';

// marketing claims supplied by design, not read from Trustpilot
export const TRUSTPILOT_RATING = 'Excellent 4.7';
export const TRUSTPILOT_REVIEW_COUNT = '14,532 Reviews';

export const INCLUDED_TITLE = 'What is included?';
export const MOST_POPULAR_LABEL = 'Most popular';

export const PRICE_COVERS_LINE = {
	label: 'YOUR PRICE COVERS:',
	body: 'provider consultation, unlimited 1:1 medical support, your prescription, a 4-week medication supply and',
	emphasis: 'free shipping'
};

export const CHECKOUT_OUTCOMES = [
	{ label: 'Support your metabolism as it adjusts to a new, healthier baseline', appendsGoalWeight: true },
	{ label: 'Look and feel healthier', appendsGoalWeight: false }
];

export const MEDICATION_SHARED_BULLETS: readonly string[] = [
	'Prescription and free delivery within days',
	'Prescribed by US-based, board-certified clinicians',
	'Message your provider anytime, 24/7'
];

export const toMedicationClaims = (badge: string, leadBullet: string): MedicationClaims => ({
	badge,
	bullets: [leadBullet, ...MEDICATION_SHARED_BULLETS]
});

export const CHECKOUT_SHIPPING_NOTICE = 'Heads up: injectable medications ship cold; home address, no P.O boxes';

export const CHECKOUT_POLICY_LINKS: readonly PolicyLink[] = [
	{ label: 'Terms of Use', href: ROUTES.TERMS },
	{ label: 'Privacy Policy', href: ROUTES.PRIVACY },
	{ label: 'Communication Policy', href: ROUTES.COMMUNICATION_POLICY }
];

// legal copy: never edit or summarise it to fit a layout
export const CHECKOUT_DISCLOSURE: readonly { label: string | null; body: string }[] = [
	{
		label: 'Compounded Medications.',
		body: 'All prescriptions require evaluation by an independent, licensed healthcare provider, who retains sole discretion to prescribe or decline to prescribe. If a provider determines a personalized, compounded medication is clinically appropriate for you, it is prepared and dispensed by an independent, state-licensed compounding pharmacy. Compounded medications are not FDA-approved: the FDA does not review compounded drugs for safety, effectiveness, or quality, and they are not generic versions of — and have not been shown to be equivalent to — any FDA-approved medication. Compounded medications are available only where the provider determines an individualized clinical need; availability varies by state. ReadyRX is not a pharmacy or drug manufacturer and does not dispense medications; product appearance may differ from images shown.'
	},
	{
		label: 'Branded Medications.',
		body: 'Your provider may instead prescribe an FDA-approved medication, fulfilled through our independent, licensed partner pharmacies. Zepbound® is FDA-approved for chronic weight management; Ozempic® is FDA-approved for type 2 diabetes and may be prescribed off-label for weight management. Ozempic®, Zepbound®, and all other trademarks are the property of their respective owners, which are not affiliated with and do not endorse ReadyRX.'
	},
	{
		label: 'Safety.',
		body: 'GLP-1 medications are prescription drugs to be taken under the supervision of a licensed healthcare provider. They carry serious risks, including a boxed warning for possible thyroid C-cell tumors, including medullary thyroid carcinoma (MTC). Do not use if you or a family member has a history of MTC or Multiple Endocrine Neoplasia syndrome type 2 (MEN 2), or if you are pregnant or planning to become pregnant. Contact your provider immediately if you experience a lump or swelling in your neck, hoarseness, trouble swallowing, shortness of breath, or severe abdominal pain. Other serious side effects may include pancreatitis, gallbladder problems, kidney injury, hypoglycemia, and changes in vision. This is not a complete list of risks; review all safety information with your provider. In an emergency, call 911.'
	},
	{
		label: 'Results:',
		body: 'Based on self-reported data from ReadyRX patients on personalized treatment plans; weight was self-reported, was not independently verified, and does not reflect a controlled clinical study. Individual results vary; weight loss is not guaranteed and depends on factors including adherence, diet, exercise, and individual biology.'
	},
	{
		label: 'Pricing:',
		body: 'Available to new customers only. Programs are automatically renewing subscriptions: your payment method is charged at the intervals disclosed at checkout until you cancel, and you may cancel at any time as described in our Terms of Use. Promotional pricing is subject to change; no cash value; not combinable with other offers. Purchase does not guarantee a prescription or any outcome; refunds are governed by our Terms of Use.'
	},
	{
		label: null,
		body: 'By using our services, you agree to our Terms of Use, Privacy Policy, and Communication Policy; in the event of any conflict between this disclosure and the Terms of Use, the Terms of Use control. If you are experiencing a medical emergency, call 911. If you are in crisis or experiencing thoughts of self-harm, call or text 988.'
	},
	{
		label: null,
		body: `© ${new Date().getFullYear()} ReadyRX™ All rights reserved.`
	}
];

// the weight-loss-goals2 survey asks "What is your weight loss goal?" under two field names
export const WEIGHT_LOSS_GOAL_FIELD_NAMES: readonly string[] = [
	'radiobuttons_F46FF420-A406-44BD-862F-2DD0042A0D11',
	'radiobuttons_29A97260-2CE0-418E-A6BB-B7D3E0FA305E'
];

export const CHECKOUT_TESTIMONIALS: readonly CheckoutTestimonial[] = [
	{
		title: 'Goodbye, food noise!',
		body: 'ReadyRX has been an amazing experience for me. My Rx really has helped me lose food noise and I have already lost 5 lbs already. This stuff really works. I am so happy with it…',
		name: 'Tammara W.'
	},
	{
		title: 'Never felt more confident',
		body: "Coming from someone who switched from a different company, I can not recommend ReadyRX enough! Their customer service is beyond accommodating and their processing times are quick. I've never felt more confident than I do now and in a short 4 weeks I'm down 10 pounds! I highly suggest if you're contemplating the lifestyle change to work with ReadyRX! You won't be disappointed…",
		name: 'Tatiana T.'
	},
	{
		title: 'I feel like myself again',
		body: 'ReadyRX treated me with such care and patience and helped me reach my goal weight. I gained my confidence again and I even fit in my wedding dress. I lost 45 pounds of baby weight and I am back!',
		name: 'Lucy K.'
	}
];
